
import { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { usePerformanceStage } from "@/hooks/usePerformanceStage";
import PersonalGoalsTab from "./PersonalGoalsTab";
import ManageTeamMembersTab from "./ManageTeamMembersTab";

const GoalSettingTab = () => {
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [supervisorName, setSupervisorName] = useState<string>("");
  const { config: performanceConfig } = usePerformanceStage();

  useEffect(() => {
    checkSupervisorStatus();
    fetchSupervisorName();
  }, []);

  const checkSupervisorStatus = async () => {
    setIsLoading(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error("Error getting user:", userError);
        setIsLoading(false);
        return;
      }

      // Check if current user is a supervisor by looking for team members
      const { data: teamMembers, error } = await supabase
        .from('users')
        .select('id')
        .eq('supervisor_id', user.id)
        .limit(1);

      if (error) {
        console.error("Error checking supervisor status:", error);
        setIsSupervisor(false);
      } else {
        setIsSupervisor(teamMembers && teamMembers.length > 0 || false);
      }
    } catch (error) {
      console.error("Error checking supervisor status:", error);
      setIsSupervisor(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSupervisorName = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error("Error getting user:", userError);
        return;
      }

      // Get current user's record to find supervisor_id
      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select('supervisor_id')
        .eq('id', user.id)
        .single();

      if (userDataError || !userData?.supervisor_id) {
        console.log("No supervisor found or error:", userDataError);
        return;
      }

      // Get supervisor's full_name using supervisor_id
      const { data: supervisorData, error: supervisorError } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', userData.supervisor_id)
        .single();

      if (supervisorError) {
        console.error("Error fetching supervisor data:", supervisorError);
        return;
      }

      if (supervisorData?.full_name) {
        setSupervisorName(supervisorData.full_name);
      }
    } catch (error) {
      console.error("Error fetching supervisor name:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse">Loading goal settings...</div>
      </div>
    );
  }

  // For team members, show only PersonalGoalsTab without tabs
  if (!isSupervisor) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Goal Setting</h2>
          {supervisorName && (
            <div className="flex items-center gap-2 text-white text-[11px]">
              <span>Supervisor:</span>
              <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                {supervisorName}
              </Badge>
            </div>
          )}
        </div>
        
        {/* Team member notification for non-supervisors */}
        <div className="border border-purple-500/30 rounded-lg p-3">
          <p className="text-white text-[13px]">
            For team members: Goals are loaded by your direct supervisor. Kindly contact your supervisor if you have any questions.
          </p>
        </div>
        
        <PersonalGoalsTab />
      </div>
    );
  }

  // For supervisors, show tabs with both Personal Goals and Manage Team Members
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Goal Setting</h2>
        
        {supervisorName && (
          <div className="flex items-center gap-2 text-white text-[11px]">
            <span>Supervisor:</span>
            <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
              {supervisorName}
            </Badge>
          </div>
        )}
      
      </div>
      
      {/* Sub-tabs */}
      <div className="bg-secondary/30 rounded-lg">
        <Tabs defaultValue="personal-goals" className="w-full">
          <div className="flex border-b border-primary/20">
            <TabsList className="bg-transparent">
              <TabsTrigger
                value="personal-goals"
                className="px-6 py-3 text-sm font-medium transition-colors data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white"
                disabled={!performanceConfig.goalsetting_stage}
              >
                Personal Goals
              </TabsTrigger>
              <TabsTrigger
                value="manage-team-members"
                className="px-6 py-3 text-sm font-medium transition-colors data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white"
                disabled={!performanceConfig.goalsetting_stage}
              >
                Manage Team Members
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="personal-goals" className="mt-4">
            {performanceConfig.goalsetting_stage ? (
              <PersonalGoalsTab />
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-gray-400 text-lg">Personal Goals is currently disabled</p>
                  <p className="text-gray-500 text-sm mt-2">Please contact your administrator for more information</p>
                </div>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="manage-team-members" className="mt-4">
            {performanceConfig.goalsetting_stage ? (
              <ManageTeamMembersTab />
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-gray-400 text-lg">Manage Team Members is currently disabled</p>
                  <p className="text-gray-500 text-sm mt-2">Please contact your administrator for more information</p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default GoalSettingTab;
